import { DataMatchPersonSnapshot } from "../../../../models/DataMatchResult";

export type ColumnOption = { value: string; label: string };

export type MatchFilterRow = {
    id: string;
    centralColumn: string;
    providerColumn: string;
    matchScore: number;
};

/** Form.Select value for the “no column chosen yet” option (must not be a real field key). */
export const matchColumnPlaceholderValue = "";

/** Label shown in match filter dropdowns when no column is selected. */
export const matchColumnPlaceholderLabel = "Select column";

/**
 * MIS Central (transform) columns — same set and order as the Transformed people grid
 * (`src/pages/establishments/view-data/transform-data.tsx`). Excludes that grid’s “Custom Fields” button column.
 * Values must stay in sync with `CentralDisplayFieldKeys` in MIS.API `DataMatchMatcher`.
 */
export const misCentralColumns: ColumnOption[] = [
    { value: "CRBC_ID", label: "CRBC ID" },
    { value: "USER_TYPE_NAME", label: "User Type" },
    { value: "IS_ACTIVE", label: "Status" },
    { value: "TITLE", label: "Title" },
    { value: "SURNAME", label: "Surname" },
    { value: "FIRST_NAME", label: "First Name" },
    { value: "LEGAL_SURNAME", label: "Legal Surname" },
    { value: "LEGAL_FIRST_NAME", label: "Legal First Name" },
    { value: "UPN", label: "UPN" },
    { value: "ADMISSION_NUMBER", label: "Admission No" },
    { value: "STAFF_CODE", label: "Staff Code" },
    { value: "YEAR", label: "Year" },
    { value: "REG_CLASS", label: "Reg Class" },
    { value: "DATE_OF_BIRTH", label: "Date of Birth" },
    { value: "GENDER", label: "Gender" },
    { value: "IS_FSM", label: "FSM" },
    { value: "DATE_FSM_EXPIRY", label: "FSM Expiry" },
    { value: "DIETARY_PREFERENCES", label: "Dietary Preferences" },
    { value: "DIETARY_NEEDS", label: "Dietary Needs" },
    { value: "PARENTAL_CONSENT", label: "Parental Consent" },
    { value: "HAS_PHOTO", label: "Has Photo" },
    { value: "PHOTO_HASH", label: "Photo Hash" },
    { value: "ESTABLISHMENT_ID", label: "Establishment ID" },
    { value: "MIS_ID", label: "MIS ID" },
    { value: "MIS_ID2", label: "MIS ID 2" },
    { value: "DATE_CREATED", label: "Date Created" },
    { value: "DATE_MODIFIED", label: "Date Modified" },
    { value: "DATE_LAST_ACTIVE", label: "Date Last Active" },
    { value: "DATE_LEAVING", label: "Date Leaving" },
];

/**
 * Incoming extract columns — aligned with the Extracted people grid (`IExtractData`) and API field ids.
 * Values must stay in sync with `ProviderDisplayFieldKeys` in MIS.API `DataMatchMatcher`.
 */
export const extractIncomingColumns: ColumnOption[] = [
    { value: "CLIENT_ID", label: "Client ID" },
    { value: "USER_TYPE_NAME", label: "User Type" },
    { value: "MIS_TYPE_ID", label: "MIS Type" },
    { value: "IS_ACTIVE", label: "Status" },
    { value: "TITLE", label: "Title" },
    { value: "SURNAME", label: "Surname" },
    { value: "FIRST_NAME", label: "First Name" },
    { value: "LEGAL_SURNAME", label: "Legal Surname" },
    { value: "LEGAL_FIRST_NAME", label: "Legal First Name" },
    { value: "UPN", label: "UPN" },
    { value: "ADMISSION_NUMBER", label: "Admission No" },
    { value: "STAFF_CODE", label: "Staff Code" },
    { value: "YEAR", label: "Year" },
    { value: "REG_CLASS", label: "Reg Class" },
    { value: "DATE_OF_BIRTH", label: "Date of Birth" },
    { value: "GENDER", label: "Gender" },
    { value: "IS_FSM", label: "FSM" },
    { value: "DATE_FSM_EXPIRY", label: "FSM Expiry" },
    { value: "DIETARY_PREFERENCES", label: "Dietary Preferences" },
    { value: "DIETARY_NEEDS", label: "Dietary Needs" },
    { value: "PARENTAL_CONSENT", label: "Parental Consent" },
    { value: "IS_APPLICANT", label: "Is Applicant" },
    { value: "HAS_PHOTO", label: "Has Photo" },
    { value: "PHOTO_ID", label: "Photo ID" },
    { value: "PHOTO_FILENAME", label: "Photo File Name" },
    { value: "PHOTO_HASH", label: "Photo Hash" },
    { value: "MIS_ID", label: "MIS ID" },
    { value: "MIS_ID2", label: "MIS ID 2" },
    { value: "USER_TYPE_ID", label: "User Type ID" },
    { value: "DATE_CREATED", label: "Date Created" },
    { value: "DATE_MODIFIED", label: "Date Modified" },
    { value: "DATE_LAST_ACTIVE", label: "Date Last Active" },
];

/** @deprecated Use extractIncomingColumns — kept for call sites migrating off MIS-type-specific lists. */
export function getProviderColumns(): ColumnOption[] {
    return extractIncomingColumns;
}

/** One empty rule row: engineer picks columns after reviewing preview data. */
export function getDefaultMatchRows(): Pick<MatchFilterRow, "centralColumn" | "providerColumn" | "matchScore">[] {
    return [{ centralColumn: matchColumnPlaceholderValue, providerColumn: matchColumnPlaceholderValue, matchScore: 100 }];
}

export function newMatchRowId(): string {
    return `mf-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function str(v: string | null | undefined) {
    return v != null && v !== "" ? v : "—";
}

function displayField(person: DataMatchPersonSnapshot, columnValue: string): string | undefined {
    const k = columnValue.trim().toUpperCase();
    const df = person.displayFields;
    if (!df) {
        return undefined;
    }
    const direct = df[k] ?? df[columnValue];
    return direct !== undefined && direct !== "" ? direct : undefined;
}

/** Display value for a MIS Central column in matched / grid views. */
export function getCentralColumnValue(columnValue: string, person: DataMatchPersonSnapshot): string {
    const fromMap = displayField(person, columnValue);
    if (fromMap !== undefined) {
        return str(fromMap);
    }
    switch (columnValue) {
        case "UPN":
            return str(person.upn);
        case "FIRSTNAME":
        case "FIRST_NAME":
            return str(person.firstName);
        case "LASTNAME":
        case "SURNAME":
        case "LEGAL_SURNAME":
            return str(person.lastName);
        case "DOB":
        case "DATE_OF_BIRTH":
            return str(person.dateOfBirth);
        case "EMAIL":
            return str(person.email);
        case "CRBC_ID":
            return str(person.crbcId);
        case "MIS_ID":
        case "ADMISSION_NUMBER":
            return str(person.misId);
        case "CLIENT_ID":
            return str(person.clientId);
        default:
            return "—";
    }
}

const extractIncomingColumnSet = new Set(extractIncomingColumns.map((c) => c.value));

/** Display value for a provider extract column. */
export function getProviderColumnValue(columnValue: string, person: DataMatchPersonSnapshot): string {
    const fromMap = displayField(person, columnValue);
    if (fromMap !== undefined) {
        return str(fromMap);
    }
    if (!extractIncomingColumnSet.has(columnValue)) {
        return "—";
    }
    switch (columnValue) {
        case "ADMISSION_NUMBER":
        case "MIS_ID":
            return str(person.misId);
        case "FIRSTNAME":
        case "FIRST_NAME":
            return str(person.firstName);
        case "SURNAME":
        case "LASTNAME":
        case "LEGAL_SURNAME":
            return str(person.lastName);
        case "DATE_OF_BIRTH":
        case "DOB":
            return str(person.dateOfBirth);
        case "EMAIL":
            return str(person.email);
        case "UPN":
            return str(person.upn);
        case "CLIENT_ID":
            return str(person.clientId);
        case "CRBC_ID":
            return str(person.crbcId);
        default:
            return "—";
    }
}
